// @flow

// SEE: https://docs.google.com/spreadsheets/d/1BAH7MRa129WFifXJ9cpHaX_Azn-oNN338yGZs-jH1xE/edit#gid=563257827

import { hpData } from './hp-data';
import { maxShaftSizes, sizeRatios } from './mechanical-factors';
import { fdFactors, fhFactors, ftFactors, bushingFactors, xbkShaftWarnings } from './thermal-factors';
import { box15Outputs, box25Outputs, mechanicalOutputs, thermalOutputs } from './outputs';
import xbkBushingTypes from './xbk-bushing-types';

import type { BoxOutput, Output } from './outputs';
import type { Factor, SpecData, SpecGearbox, SpecResult } from './types';
import type { FlashType } from '../../components/Flash';

//-----------//
// Constants //
//-----------//

const {
  asin,
  floor,
  PI,
  tan
} = Math;

export const BUSHING_PREFIX = 'VOR XBK';
export const HORSEPOWER_CONSTANT = 5252; // SEE: https://auto.howstuffworks.com/question622.htm
export const MAX_RPM_CALC = 65;

//---------//
// Helpers //
//---------//

export function precisionFloor(number: number, factor: number = 10): number {
  return floor(number / factor) * factor;
}

export function radians(degrees: number): number {
  return degrees * (PI / 180);
}

//----------------------//
// Conveyor calcuations //
//----------------------//

export function calcWeightOnConveyor(tonsPerHour: number = 0, conveyorLength: number = 0, conveyorSpeed: number = 0): number {
  return tonsPerHour * conveyorLength * 2000 / (conveyorSpeed * 60);
}

export function calcConveyorAngle(conveyorRise: number = 0, conveyorLength: number = 0): number {
  return asin(conveyorRise / conveyorLength) * 180 / PI;
}

export function calcHeadPulleyTorque(weightOnConveyor: number = 0, conveyorLength: number = 0, headPulleyDiameter: number = 0): number {
  return (((0.025 * weightOnConveyor) + (weightOnConveyor * (tan(radians(conveyorLength))))) * (headPulleyDiameter / 2)) / 12;
}

export function calcOutputRPM(conveyorSpeed: number = 0, headPulleyDiameter: number = 0): number {
  return conveyorSpeed * 12 / (PI * headPulleyDiameter);
}

export function caclRequiredHP(headPulleyTorque: number = 0, outputRPM: number = 0): number {
  return headPulleyTorque * outputRPM / HORSEPOWER_CONSTANT;
}

export function calcRequiredOutputHP(requiredHP: number = 0, serviceFactor: number = 0): number {
  return requiredHP * serviceFactor;
}

//---------------//
// Gearbox specs //
//---------------//

/**
 * Calculates possible ratings for interpolation based on outputRPM.
 *
 * @see https://docs.google.com/spreadsheets/d/1BAH7MRa129WFifXJ9cpHaX_Azn-oNN338yGZs-jH1xE/edit#gid=1811837022&range=34:34
 */
export function calcRPMTables(outputRPM: number = 0): Array<number> {
  const minRPM = precisionFloor(outputRPM, 5);
  const index = minRPM / 5;
  return hpData.map(function(row) {
    let [min, max] = row['values'].slice(index - 1, index + 1);
    if (min == null) {
      min = 0;
    }
    if (max == null) {
      max = 0;
    }
    return ((max - min) / 5) * (outputRPM - minRPM) + min;
  });
}

export function calcGearboxSize(requiredOutputHP: number = 0, tables: Array<number>, boxOutputs: BoxOutput): Array<?number> {
  // console.log('tables', tables);

  // Find min/max ratio that matches required output HP
  const outputs: Array<Array<?number>> = Object.keys(boxOutputs).map(function(key: string) {
    const index = parseInt(key, 10);
    return [tables[index - 1], boxOutputs[key]];
  });

  // console.log('outputs', outputs);

  // Get the ratios only
  const values: Array<?number> = outputs.map(i => i[0]);

  // console.log('values', values);

  // Find the closest ratio match
  const closest: number = values.findIndex(function(value) {
    if (value != null) {
      return value > requiredOutputHP;
    }
    return false;
  });

  if (closest !== -1) {
    return outputs[closest];
  }

  return [];
}

export function calcHPOutput(gearbox:?number, tables: Array<number>, outputs: Output):?number {
  if (gearbox == null) {
    return;
  }

  const index = outputs[gearbox.toString()];
  const output = tables[(index || 0) - 1];

  return output;
}

export function calcGearboxData(outputRPM: number = 0, requiredOutputHP: number, motorSpeed: number): SpecGearbox {
  const tables = calcRPMTables(outputRPM);

  const [hp15, gearbox15] = calcGearboxSize(requiredOutputHP, tables, box15Outputs);
  const [hp25, gearbox25] = calcGearboxSize(requiredOutputHP, tables, box25Outputs);

  let gearbox = gearbox15;
  let horsepower = hp15;

  if (!gearbox) {
    gearbox = gearbox25;
    horsepower = hp25;
  }

  // best box based on sheave ratio being lowest but still > 1
  const ratio = motorSpeed / (outputRPM * sizeRatios[gearbox25]);
  if (ratio - 1 > 0) {
    gearbox = gearbox25;
  }

  const thermal = calcHPOutput(gearbox, tables, thermalOutputs);
  const mechanical = calcHPOutput(gearbox, tables, mechanicalOutputs);

  return {
    gearbox,
    horsepower,
    mechanical,
    ratio,
    thermal
  };
}

//---------------//
// Thermal Specs //
//---------------//

export function calcMinMax(input: number = 0, factors: Factor):?Factor {
  const keys = factors.map(f => f[0]);
  const closest = keys.findIndex(function(value) {
    return value >= input;
  });
  let minMax: Factor = [factors[closest - 1], factors[closest]];
  if (minMax[0] == null || minMax[1] == null) {
    return;
  }
  return minMax;
}

export function calcFactor(input: number = 0, factors: Factor): number {
  // console.log('input', input);
  // console.log('factors', factors);

  const minMax = calcMinMax(input, factors);
  if (!minMax) {
    return 0;
  }
  // console.log(minMax);

  const [[minKey, minFactor], [maxKey, maxFactor]] = minMax;
  // console.log(minKey, maxKey, minFactor, maxFactor);

  return (((maxFactor - minFactor) / (maxKey - minKey)) * (input - minKey)) + minFactor;
}

export function getBushingFactor(value: number): ?[number, string] {
  return bushingFactors.find(function(factor) {
    return factor[0] === value;
  });
}

export function getBushingFraction(value: number): ?string {
  const factor = getBushingFactor(value);
  if (factor != null) {
    return factor[1];
  }
}

export function calcBushingSize(headPulleyShaftDiameter: number, maxShaftSize: number): ?[number, string] {
  const value = parseFloat(parseInt(headPulleyShaftDiameter, 10) === 0 ? maxShaftSize : headPulleyShaftDiameter);

  // console.log('headPulleyShaftDiameter', headPulleyShaftDiameter);
  // console.log('maxShaftSize', maxShaftSize);
  // console.log('value', value);

  return getBushingFactor(value);
}

//----------//
// Warnings //
//----------//

function warning(message: string): FlashType {
  return {
    message: `<strong>Warning:</strong> ${message}`,
    type: 'warning'
  };
}

export function commonSizes(bushings: { [string]: string }): Array<[number, string]> {
  const stock = [];

  Object.keys(bushings).forEach(function(bushing) {
    if (bushings[bushing] === 'STOCK') {
      const size = bushingFactors.find(function(factor) {
        return factor[1] === bushing;
      });
      if (size) {
        stock.push(size);
      }
    }
  });

  return stock;
}

export function processWarnings(result: SpecResult): { [key: string]: FlashType } {
  const warnings: { [string]: FlashType } = {};
  const {
    bushings,
    bushingDec,
    hpet,
    inputHPCap,
    isMTO,
    isValidBushing,
    maxShaftSize,
    series,
    smr
  } = result;

  if (hpet != null && inputHPCap != null && hpet < inputHPCap) {
    warnings['cooling'] = warning('Thermal capacity is less than mechanical capacity; consider cooling options or increasing gearbox size.');
  }

  if (bushingDec != null && smr != null && series != null) {
    if (bushingDec > maxShaftSize) {
      warnings['shaftSize'] = warning('Shaft size is larger than gearbox max; consider increasing gearbox size.');
    }

    if (!isValidBushing || isMTO) {
      const sizes = commonSizes(bushings).map(size => `(${size[0]}")`).join(' OR ');
      if (!isValidBushing) {
        warnings['bushingCatalog'] = warning(`<em>${bushingDec}"</em> is not a catalog bushing size for <em>${smr}</em>. Use shaft size of ${sizes} for common bushing size.`);
      }
      else {
        warnings['bushingCatalog'] = warning(`<em>${bushingDec}"</em> is a made-to-order bushing size for <em>${smr}</em>. Common bushing sizes for <em>${smr}</em> include ${sizes}.`);
      }
    }

    const xbkData = xbkShaftWarnings[`XBK${series}`];
    if (bushingDec <= xbkData[0]) {
      warnings['loadCapacity'] = warning(`Check the head pulley shaft and key for adequate torque/load capacity or use a shaft size of <em>${xbkData[1]}</em>" or larger.`);
    }
  }

  return warnings;
}

//----------------------//
// Primary calculations //
//----------------------//

export default function calculate(specs: SpecData): SpecResult {
  // console.profile('calculate');

  const {
    ambientTemp = 0,
    altitude = 0,
    conveyorLength = 0,
    conveyorRise = 0,
    conveyorSpeed = 0,
    dutyCycle = 0,
    headPulleyDiameter = 0,
    motorSpeed = 0,
    tonsPerHour = 0
  } = specs;

  // Prep options
  let {
    headPulleyShaftDiameter = 0,
    serviceFactor = 0
  } = specs;
  headPulleyShaftDiameter = parseFloat(headPulleyShaftDiameter);
  serviceFactor = parseFloat(serviceFactor);

  // Conveyor calculations
  const weightOnConveyor = calcWeightOnConveyor(tonsPerHour, conveyorLength, conveyorSpeed);
  const conveyorAngle = calcConveyorAngle(conveyorRise, conveyorLength);
  const headPulleyTorque = calcHeadPulleyTorque(weightOnConveyor, conveyorAngle, headPulleyDiameter);
  const outputRPM = calcOutputRPM(conveyorSpeed, headPulleyDiameter);
  const requiredHP = caclRequiredHP(headPulleyTorque, outputRPM);
  const requiredOutputHP = calcRequiredOutputHP(requiredHP, serviceFactor);

  // Selected gearbox specs
  const {
    gearbox,
    horsepower: outputHPCap,
    mechanical: inputHPCap,
    thermal: hpt
  } = calcGearboxData(outputRPM, requiredOutputHP, motorSpeed);

  const actualSMRRatio = sizeRatios[gearbox];
  const sheaveRatio = motorSpeed / (actualSMRRatio * outputRPM);
  const maxShaftSize = maxShaftSizes[gearbox];
  const actualSF = (outputHPCap || 0) / requiredHP;

  // Thermal factors
  const ft = calcFactor(ambientTemp, ftFactors);
  const fd = calcFactor(dutyCycle, fdFactors);
  const fh = calcFactor(altitude, fhFactors);
  const fv = parseFloat(specs.spaceDescription) || 0;
  const hpet = (hpt || 0) * ft * fd * fh * fv;

  let backstop;
  let bushingDec;
  let bushingFrac;
  let bushingKit;
  let bushingKitShort;
  let bushings = {};
  let isMTO = false;
  let isStock = false;
  let isValidBushing = false;
  let motorMount;
  let series;
  let smr;

  if (gearbox != null) {
    series = parseInt(gearbox.toString()[0], 10);
    const bushing = calcBushingSize(headPulleyShaftDiameter, maxShaftSize);

    if (bushing != null) {
      [bushingDec, bushingFrac] = bushing;
    }

    smr = `VOR VXT${gearbox} XD`;
    bushingKit = `${BUSHING_PREFIX}${series}`;
    bushings = xbkBushingTypes[bushingKit];

    if (bushingFrac != null) {
      bushingKit += ` ${bushingFrac}`;

      isStock = bushings[bushingFrac] === 'STOCK';
      isMTO = bushings[bushingFrac] === 'MTO';
      isValidBushing = isStock || isMTO;
    }

    bushingKitShort = bushingKit;

    if (bushingDec != null) {
      bushingKit += ` (${bushingDec})`;
    }

    motorMount = `VOR ${series}MMA`;
    backstop = `VOR ${series}BKS`;
  }

  // console.profileEnd();

  return {
    weightOnConveyor,
    conveyorAngle,
    headPulleyTorque,
    outputRPM,
    requiredHP,
    serviceFactor,
    requiredOutputHP,
    gearbox,
    outputHPCap,
    inputHPCap,
    actualSMRRatio,
    sheaveRatio,
    maxShaftSize,
    actualSF,
    hpt,
    ft,
    fd,
    fh,
    fv,
    hpet,
    smr,
    series,
    bushingDec,
    bushingFrac,
    bushingKit,
    bushingKitShort,
    bushings,
    isMTO,
    isStock,
    isValidBushing,
    motorMount,
    backstop
  };
}



// WEBPACK FOOTER //
// ./src/utils/vortex/calculate.js